import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import SopotakContext from "../../../store/sopotak-context";
import OrderListPanel from "./OrderListPanel";
import { Container, Row, Col } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import "../../../styles.css";
import config from "../../../api/api-helper";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

const OrderPane = (props) => {
  const {
    tripType,
    from,
    to,
    fromDate,
    toDate,
    persons,
    children,
    bikes,
    departureOffer,
    returnOffer,
    duration,
    setDepartureOffer,
    setReturnOffer,
    offersData,
  } = useContext(SopotakContext);

  const { isWinterSeason } = config;

  const { i18n, t } = useTranslation();

  const [selectedDepartureEndTime, setSelectedDepartureEndTime] =
    useState(null);
  const [selectedReturnStartTime, setSelectedReturnStartTime] = useState(null);

  const [additionalOffersData, setAdditionalOffersData] = useState(null);

  const [isLoadingReturnOffers, setIsLoadingReturnOffers] = useState(false);

  const [isSecondStep, setIsSecondStep] = useState(false);

  const departureListRef = useRef(null);
  const nextDateRef = useRef(null);
  const noOffersMessageRef = useRef(null);
  const returnListRef = useRef(null);

  useEffect(() => {
    setSelectedDepartureEndTime(null);
    setSelectedReturnStartTime(null);
    setIsSecondStep(false);
    setAdditionalOffersData(null);

    if (offersData?.departureOffers?.length && departureListRef.current) {
      // Scroll to the list of offers if available
      departureListRef.current.scrollIntoView({ behavior: "smooth" });
    } else if (!offersData?.departureOffers?.length) {
      // Scroll to next available date if exists
      if (offersData?.nextAvailableDate && nextDateRef.current) {
        nextDateRef.current.scrollIntoView({ behavior: "smooth" });
      }
      // Scroll to no offers message if no offers and no next available date
      else if (!offersData?.nextAvailableDate && noOffersMessageRef.current) {
        noOffersMessageRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [offersData]);

  useEffect(() => {
    setSelectedDepartureEndTime(null);
    setSelectedReturnStartTime(null);

    if (additionalOffersData?.returnOffers?.length && returnListRef.current) {
      returnListRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [additionalOffersData]);

  const submitHandler = (event) => {
    event.preventDefault();
    props.progressHandler(2);
  };

  const backButtonHandler = (event) => {
    event.preventDefault();
    setIsSecondStep(false);
    setSelectedReturnStartTime(null);
  };

  const handleNextStep = (event) => {
    event.preventDefault();
    if (!departureOffer) return;
    setIsSecondStep(true);

    let firstLegArrivalTime = new Date(departureOffer.arrivalTime);

    const body = {
      type: tripType,
      departureLocation: from,
      departureTime: fromDate,
      arrivalLocation: to,
      numberOfPersons: persons,
      numberOfChildren: children,
      numberOfBikes: bikes,
      durationInMinutes: duration,
      returnTime: toDate,
      arrivalTime: firstLegArrivalTime,
    };

    fetchOffers(body);
  };

  const handleDepartureOffer = useCallback(
    (offer) => {
      setDepartureOffer(offer);
      if (offer) {
        setSelectedDepartureEndTime(new Date(offer.arrivalTime).getTime());
      } else {
        setSelectedDepartureEndTime(null);
      }
    },
    [setDepartureOffer]
  );

  const handleReturnOffer = useCallback(
    (offer) => {
      setReturnOffer(offer);
      if (offer) {
        setSelectedReturnStartTime(new Date(offer.departureTime).getTime());
      } else {
        setSelectedReturnStartTime(null);
      }
    },
    [setReturnOffer]
  );

  const fetchOffers = useCallback((body) => {
  setIsLoadingReturnOffers(true);

  const {
    type,
    departureLocation,
    departureTime,
    arrivalLocation,
    returnTime,
    numberOfPersons,
    numberOfChildren,
    numberOfBikes,
    arrivalTime,
  } = body;

  const url =
    `${config.apiUrl}/api/sopotak/v1/offers?` +
    `type=${encodeURIComponent(type)}` +
    `&departureLocation=${encodeURIComponent(departureLocation)}` +
    `&departureTime=${encodeURIComponent(new Date(departureTime).toISOString())}` +
    `&arrivalLocation=${encodeURIComponent(arrivalLocation)}` +
    `&numberOfPersons=${encodeURIComponent(numberOfPersons)}` +
    `&numberOfChildren=${encodeURIComponent(numberOfChildren)}` +
    `&numberOfBikes=${encodeURIComponent(numberOfBikes)}` +
    `&returnTime=${encodeURIComponent(new Date(returnTime).toISOString())}` +
    `&arrivalTime=${encodeURIComponent(new Date(arrivalTime).toISOString())}`;

  fetch(url)
    .then((r) => r.json())
    .then((data) => {
        setAdditionalOffersData(data);
      })
      .catch((error) => {
        console.error("Error fetching offers:", error);
  })
      .finally(() => {
        setIsLoadingReturnOffers(false);
      });
  }, []);

  const formatLocalizedDate = useCallback(
  (isoDate) => {
    const dt = DateTime.fromISO(isoDate, { zone: "Europe/Warsaw" });
    return i18n.language === "en"
      ? dt.toFormat("MM/dd/yyyy")
      : dt.toFormat("dd.MM.yyyy");
  },
  [i18n.language]
);

  const renderOffers = () => {
    const departureOffersExist = offersData?.departureOffers?.length > 0;
    const returnOffersExist = additionalOffersData?.returnOffers?.length > 0;
    const nextAvailableDateDeparture = offersData?.nextAvailableDate;
    const nextAvailableDateReturn = additionalOffersData?.nextAvailableDate;

    if (!offersData) {
      return null;
    }

    if (isLoadingReturnOffers) {
      return (
        <Container fluid>
          <Row className="justify-content-center">
            <Col xs="auto">
              <h5 ref={noOffersMessageRef}>{t("orderPane.loading")}</h5>
            </Col>
          </Row>
        </Container>
      );
    }

    // Case: No departure offers, check nextAvailableDate and show appropriate message
    if (!departureOffersExist && !isSecondStep) {
      if (
        nextAvailableDateDeparture !== null &&
        nextAvailableDateDeparture !== undefined
      ) {
        // Check if nextAvailableDate is a valid date
        const validDate = new Date(nextAvailableDateDeparture);
        if (!isNaN(validDate.getTime())) {
          // Case: No offers but a valid next available date exists
          return (
            <Container fluid>
              <Row className="justify-content-center text-center">
                <Col xs="auto">
                  <h5 ref={nextDateRef}>
                    {t("orderPane.noOffersDeparture", {
                      date: formatLocalizedDate(nextAvailableDateDeparture),
                    })}
                  </h5>
                </Col>
              </Row>
            </Container>
          );
        }
      }

      // Case: No offers and no valid next available date (nextAvailableDate is null or undefined)
      return (
        <Container fluid>
          <Row className="justify-content-center">
            <Col xs="auto">
              <h5 ref={noOffersMessageRef}>
                {isWinterSeason ? t("orderPane.noOffersFallback") :t("orderPane.noOffersDefault")}
              </h5>
            </Col>
          </Row>
        </Container>
      );
    }

    // Case: Departure offers exist, but returnOffers empty, check nextAvailableDate and show appropriate message
    if (!returnOffersExist && isSecondStep) {
      if (
        nextAvailableDateReturn !== null &&
        nextAvailableDateReturn !== undefined
      ) {
        // Check if nextAvailableDate is a valid date
        const validDate = new Date(nextAvailableDateReturn);
        if (!isNaN(validDate.getTime())) {
          // Case: No offers but a valid next available date exists
          return (
            <Container fluid>
              <Row className="justify-content-center text-center">
                <Col xs="auto">
                  <h5 ref={nextDateRef}>
                    {t("orderPane.noOffersReturn", {
                      date: formatLocalizedDate(nextAvailableDateReturn),
                    })}
                  </h5>
                </Col>
              </Row>
            </Container>
          );
        }
      }

      // Case: No return offers and no valid next available date (nextAvailableDate is null or undefined)
      return (
        <Container fluid>
          <Row className="justify-content-center">
            <Col xs="auto">
              <h5 ref={noOffersMessageRef}>
                {t("orderPane.noOffersDefault")}
              </h5>
            </Col>
          </Row>
        </Container>
      );
    }

    // Case: Departure offers exist, so display departures only first
    if (departureOffersExist && !isSecondStep) {
      return (
        <div className="form-wrapper">
          <Container fluid>
            <Row className="mb-2">
              <Col className="col-12 gx-2">
                <OrderListPanel
                  ref={departureListRef}
                  orderList={offersData.departureOffers}
                  direction={t("orderPane.directionOut")}
                  date={offersData.departureDate}
                  offerHandler={handleDepartureOffer}
                  selectedReturnStartTime={selectedReturnStartTime}
                  isFirstLeg={true}
                />
              </Col>
            </Row>

            {tripType === "RETURN" ? (
              <Row>
                <Col className="col-12 gx-2">
                  <Button
                    size="lg"
                    onClick={handleNextStep}
                    className="w-100"
                    disabled={!departureOffer}
                  >
                    {t("orderPane.next")}
                  </Button>
                </Col>
              </Row>
            ) : (
              <Row>
                <Col className="col-12 gx-2">
                  <Button
                    size="lg"
                    onClick={submitHandler}
                    className="w-100"
                    disabled={!departureOffer}
                  >
                    {t("orderPane.book")}
                  </Button>
                </Col>
              </Row>
            )}
          </Container>
        </div>
      );
    } else if (departureOffersExist && returnOffersExist && isSecondStep) {
      return (
        <div className="form-wrapper">
          <Container fluid>
            <Row className="mb-2">
              <Col className="col-12 gx-2">
                <OrderListPanel
                  ref={returnListRef}
                  orderList={additionalOffersData.returnOffers}
                  direction={t("orderPane.directionBack")}
                  date={additionalOffersData.arrivalDate}
                  offerHandler={handleReturnOffer}
                  selectedDepartureEndTime={selectedDepartureEndTime}
                  nextAvailableDate={additionalOffersData.nextAvailableDate}
                  isFirstLeg={false}
                />
              </Col>
            </Row>
            <Row>
              <Col className="col-6 gx-2">
                <Button size="lg" onClick={backButtonHandler} className="w-100">
                  {t("orderPane.back")}
                </Button>
              </Col>
              <Col className="col-6 gx-2">
                <Button
                  size="lg"
                  onClick={submitHandler}
                  className="w-100"
                  disabled={tripType === "RETURN" && !returnOffer}
                >
                  {t("orderPane.book")}
                </Button>
              </Col>
            </Row>
          </Container>
        </div>
      );
    }
  };

  return (
    <div>
      <section>{renderOffers()}</section>
    </div>
  );
};

export default OrderPane;
